// =====================================================================================
// INVRT Radar - web console - campaign audience action/state surface (AU09-UI)
//
// The wired half of the campaign Audience tab: it replaces the accepted read-only tab's
// "not available in this workspace yet" placeholder card and wires the AU05-AU08 bridge
// routes through the ONE named hook (src/hooks/useAudience.ts) on the existing
// useApiClient. The read-only aggregate preview above it is NOT rebuilt and stays in
// AudienceTab exactly as accepted.
//
// Every PRD_03 audience state has an honest rendering with its permitted next actions:
// setup-required, ready, empty (the preview above), held, unavailable, stale/conflict,
// running, unknown, failed, generated, download-ineligible, handoff (manual, never
// remote-confirmed) and the remote-confirmed label rendered verbatim only if a receipt
// ever carries one (no producer of it exists anywhere in the estate today).
// Laws: render/refetch never generates, bills, uploads or retries; an org switch
// cancels stale actions and can neither display another org's receipt nor send an
// action with old scope; counts explain exclusions without supplier names.
// =====================================================================================

import { useState } from "react";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { isApiError } from "@/lib/api";
import {
  type AudiencePlatform,
  type AudienceSegmentation,
} from "@/lib/types";
import {
  AudienceScopeChangedError,
  useAudience,
} from "@/hooks/useAudience";

const PLATFORMS: AudiencePlatform[] = [
  "meta",
  "google_contact",
  "google_address",
  "tiktok",
  "linkedin",
];

/** The version label vocabulary copy. A label outside the map renders verbatim, so a
 * remote-confirmed label, if one is ever produced by a separately commissioned
 * adapter, appears as itself and never as a claim this service made. */
const VERSION_STATE_COPY: Record<string, string> = {
  generated: "Generated",
  manual_handoff_recorded: "Manual handoff recorded",
  removal_unverified: "Removal requested, not verified",
  superseded: "Superseded",
};

function versionStateCopy(label: string): string {
  return VERSION_STATE_COPY[label] ?? label;
}

function formatBytes(count: number): string {
  if (count < 1024) return `${count} B`;
  if (count < 1024 * 1024) return `${(count / 1024).toFixed(1)} KB`;
  return `${(count / (1024 * 1024)).toFixed(1)} MB`;
}

type ConfigFailure = "setup" | "held" | "unavailable";

function classifyConfigFailure(error: unknown): ConfigFailure {
  if (isApiError(error)) {
    // The bridge's hand-built 409 bodies (audience_config_not_configured) flatten to
    // httpStatus 409 with a generic token in this client; on the read route 409 means
    // exactly one thing: nothing is configured yet.
    if (error.httpStatus === 409) return "setup";
    if (error.httpStatus === 403 || error.token === "forbidden") return "held";
  }
  return "unavailable";
}

function isHeldActionError(error: unknown): boolean {
  return isApiError(error) && (error.httpStatus === 403 || error.token === "forbidden");
}

function isActionUnavailableError(error: unknown): boolean {
  if (!isApiError(error)) return !(error instanceof AudienceScopeChangedError);
  return (
    error.token === "audience_export_unavailable" ||
    error.token === "audience_config_unavailable" ||
    error.token === "provider_unavailable" ||
    error.httpStatus === 502 ||
    error.httpStatus === 503
  );
}

function isStaleActionError(error: unknown): boolean {
  return isApiError(error) && error.httpStatus === 409;
}

export function AudienceActions({
  campaignId,
  campaignKey,
}: {
  campaignId: string;
  campaignKey: string;
}) {
  const { session, orgId } = useSession();
  const audience = useAudience(campaignId, campaignKey);
  const actor = session?.user.id ?? null;

  // Editor state: never prefilled and never inferred from the configuration - a save
  // carries exactly what the operator chose, plus the required reason.
  const [editorPlatforms, setEditorPlatforms] = useState<AudiencePlatform[]>([]);
  const [editorSegmentation, setEditorSegmentation] = useState<AudienceSegmentation>("all");
  const [editorSegmentValues, setEditorSegmentValues] = useState("");
  const [editorReason, setEditorReason] = useState("");

  if (!actor || !orgId) {
    return (
      <EmptyState
        title="Choose an organisation"
        body="Select your organisation to view its assigned campaign audience."
      />
    );
  }

  const { config, configData, saveConfig, generate, history, receipts } = audience;
  const refreshConfig = () => void config.refetch();

  const togglePlatform = (platform: AudiencePlatform) => {
    setEditorPlatforms((current) =>
      current.includes(platform)
        ? current.filter((p) => p !== platform)
        : [...current, platform],
    );
  };

  const reasonReady = editorReason.trim().length > 0;
  const canSave = reasonReady && editorPlatforms.length > 0 && !saveConfig.isPending;
  const segmentValues =
    editorSegmentation === "all"
      ? undefined
      : editorSegmentValues
          .split(",")
          .map((value) => value.trim())
          .filter((value) => value.length > 0);
  const canSaveSegmented = editorSegmentation === "all" || (segmentValues?.length ?? 0) > 0;
  // The editor is the setup path when nothing is configured; it never renders while the
  // configuration is unverified (held/unavailable), where a CAS version would be a guess.
  const configFailure: ConfigFailure | null = config.isError
    ? classifyConfigFailure(config.error)
    : null;
  const editorVisible = configData !== null || configFailure === "setup";

  const save = (kind: "save-default" | "clear-default" | "save-override" | "clear-override") => {
    saveConfig.mutate(
      {
        kind,
        ...(kind === "save-default" || kind === "save-override"
          ? {
              platforms: editorPlatforms,
              segmentation: editorSegmentation,
              ...(segmentValues ? { segmentValues } : {}),
            }
          : {}),
        reason: editorReason.trim(),
      },
      { onSuccess: () => void config.refetch() },
    );
  };

  const configSource = configData
    ? configData.override_applied
      ? "Campaign override"
      : "Organisation default"
    : null;

  const attemptStatus = history.data?.status ?? generate.data?.status ?? null;
  const files = history.data?.files ?? generate.data?.files ?? [];
  const attemptBusy = generate.isPending || attemptStatus === "running";
  const newAttempt = () => {
    audience.startNewAttempt();
    // The explicit "new attempt" action mints the fresh id and posts it in the same
    // handler; nothing else in this surface ever mints one.
    generate.mutate(crypto.randomUUID());
  };

  // The CAS editor: the setup path when nothing is configured; it never renders while
  // the configuration is unverified (held/unavailable), where a CAS version would be a
  // guess. A save carries exactly what the operator chose, plus the required reason.
  const editorSection = editorVisible ? (
    <div>
      {saveConfig.isError && !(saveConfig.error instanceof AudienceScopeChangedError) ? (
        <div role="alert" className="mt-4 rounded-xl border border-line p-4">
          {isStaleActionError(saveConfig.error) ? (
            <p className="text-sm text-ink">
              The configuration changed elsewhere, or there was nothing to change. Nothing was
              written. Refresh to load the current version, then reapply.
            </p>
          ) : isHeldActionError(saveConfig.error) ? (
            <p className="text-sm text-ink">
              The change was refused for this organisation or campaign. Check your assignment;
              nothing was written.
            </p>
          ) : (
            <p className="text-sm text-ink">
              The configuration service is unavailable; nothing was changed. Refresh to try the
              read again first.
            </p>
          )}
        </div>
      ) : null}
      <div className="mt-4 space-y-3 rounded-xl border border-dashed border-line p-4">
        <p className="text-sm text-ink-2">
          Changes apply through the configuration service with your stated reason and the
          version it was checked against. A refused save is never retried silently.
        </p>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-ink">Platforms for the save</legend>
          {PLATFORMS.map((platform) => (
            <label
              key={platform}
              className="flex min-h-[44px] items-center gap-3 text-sm text-ink"
            >
              <input
                type="checkbox"
                className="h-[44px] w-[44px] accent-primary"
                checked={editorPlatforms.includes(platform)}
                onChange={() => togglePlatform(platform)}
              />
              {platform}
            </label>
          ))}
        </fieldset>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-ink">Segmentation for the save</legend>
          {(["all", "tag", "intent"] as AudienceSegmentation[]).map((mode) => (
            <label key={mode} className="flex min-h-[44px] items-center gap-3 text-sm text-ink">
              <input
                type="radio"
                name="audience-segmentation"
                className="h-[44px] w-[44px] accent-primary"
                checked={editorSegmentation === mode}
                onChange={() => setEditorSegmentation(mode)}
              />
              {mode}
            </label>
          ))}
        </fieldset>
        {editorSegmentation !== "all" ? (
          <div>
            <label htmlFor="audience-segment-values" className="text-sm font-medium text-ink">
              Segment values (comma separated)
            </label>
            <input
              id="audience-segment-values"
              type="text"
              className="mt-1 min-h-[44px] w-full rounded-lg border border-line bg-surface px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              value={editorSegmentValues}
              onChange={(event) => setEditorSegmentValues(event.target.value)}
            />
          </div>
        ) : null}
        <div>
          <label htmlFor="audience-reason" className="text-sm font-medium text-ink">
            Reason
          </label>
          <input
            id="audience-reason"
            type="text"
            className="mt-1 min-h-[44px] w-full rounded-lg border border-line bg-surface px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            value={editorReason}
            onChange={(event) => setEditorReason(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="primary"
            className="min-h-[44px] min-w-[44px]"
            disabled={!canSave || !canSaveSegmented}
            loading={saveConfig.isPending}
            onClick={() => save("save-default")}
          >
            Save org default
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="min-h-[44px] min-w-[44px]"
            disabled={!canSave || !canSaveSegmented}
            loading={saveConfig.isPending}
            onClick={() => save("save-override")}
          >
            Save campaign override
          </Button>
          {configData?.override_present ? (
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px] min-w-[44px]"
              disabled={!reasonReady || saveConfig.isPending}
              loading={saveConfig.isPending}
              onClick={() => save("clear-override")}
            >
              Clear campaign override
            </Button>
          ) : null}
          {configData && configData.org_version !== null ? (
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px] min-w-[44px]"
              disabled={!reasonReady || saveConfig.isPending}
              loading={saveConfig.isPending}
              onClick={() => save("clear-default")}
            >
              Clear org default
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <section aria-labelledby="audience-actions-title" className="space-y-6">
      <div className="rounded-xl border border-line p-5">
        <h3 id="audience-actions-title" className="font-semibold text-ink">Audience files and settings</h3>
        <p className="mt-2 text-sm text-ink-2">
          The effective configuration, file generation and receipts for this assigned campaign.
          Rendering a page never generates a file; generation starts only from the button below.
          A generated file and a confirmed platform upload are separate outcomes.
        </p>

        {/* ---- Configuration ------------------------------------------------------- */}
        <div aria-live="polite" aria-busy={config.isFetching} className="mt-4">
          {config.isPending || config.isFetching ? (
            config.isPending ? (
              <LoadingState label="Checking audience configuration…" />
            ) : null
          ) : config.isError ? (
            (() => {
              if (configFailure === "setup") {
                return (
                  <EmptyState
                    tone="empty"
                    title="Audience setup required"
                    body="No audience configuration exists yet for this organisation or campaign. Save an organisation default or a campaign override below to set it up; nothing has been configured by this page."
                  />
                );
              }
              if (configFailure === "held") {
                return (
                  <EmptyState
                    tone="error"
                    title="Audience actions held"
                    body="Your current organisation or campaign access could not be verified. Check your assignment before trying again; no audience action was requested and nothing was changed."
                  />
                );
              }
              return (
                <EmptyState
                  tone="error"
                  title="Audience configuration unavailable"
                  body="The configuration could not be verified. Refresh to check again; no audience action was requested and nothing was changed."
                  action={
                    <Button
                      type="button"
                      variant="secondary"
                      className="min-h-[44px] min-w-[44px]"
                      disabled={config.isFetching}
                      onClick={refreshConfig}
                    >
                      Refresh configuration
                    </Button>
                  }
                />
              );
            })()
          ) : configData ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-ink">
                  Source: <span className="font-semibold">{configSource}</span>
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-[44px] min-w-[44px]"
                  disabled={config.isFetching}
                  onClick={refreshConfig}
                >
                  Refresh configuration
                </Button>
              </div>
              <dl className="grid gap-3 sm:grid-cols-2">
                <div>
                  <dt className="text-sm text-ink-2">Configured platforms</dt>
                  <dd className="mt-1 flex flex-wrap gap-2">
                    {(configData.effective_platforms ?? []).map((platform) => (
                      <span
                        key={platform}
                        className="rounded-full border border-line px-2 py-0.5 text-sm text-ink"
                      >
                        {platform}
                      </span>
                    ))}
                    {(configData.effective_platforms ?? []).length === 0 ? (
                      <span className="text-sm text-ink-muted">None shown</span>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-ink-2">Segmentation</dt>
                  <dd className="mt-1 text-sm text-ink">
                    {configData.effective_segmentation ?? "Not set"}
                    {configData.effective_segment_values &&
                    configData.effective_segment_values.length > 0
                      ? ` (${configData.effective_segment_values.join(", ")})`
                      : null}
                  </dd>
                </div>
              </dl>
            </div>
          ) : null}
        </div>

        {editorVisible && editorSection}

        {/* ---- Generation ---------------------------------------------------------- */}
        {configData?.configured && !configData.setup_required ? (
          <div className="mt-6 space-y-3 rounded-xl border border-line p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h4 className="font-semibold text-ink">Export files</h4>
              {attemptStatus !== null ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-[44px] min-w-[44px]"
                  disabled={attemptBusy}
                  onClick={() => {
                    void history.refetch();
                    void receipts.refetch();
                  }}
                >
                  Refresh status
                </Button>
              ) : null}
            </div>
            {attemptStatus === null ? (
              <div className="space-y-3">
                <p className="text-sm text-ink-2">
                  One attempt renders this campaign's configured platforms from its pinned
                  selection. Starting is the only thing that renders; looking at this page, or
                  refreshing it, never does.
                </p>
                <Button
                  type="button"
                  variant="primary"
                  className="min-h-[44px] min-w-[44px]"
                  disabled={generate.isPending}
                  loading={generate.isPending}
                  onClick={() => generate.mutate(undefined)}
                >
                  Generate files
                </Button>
              </div>
            ) : attemptBusy ? (
              <LoadingState label="Generating files…" />
            ) : null}
            {generate.isError && !(generate.error instanceof AudienceScopeChangedError) ? (
              <div role="alert" className="rounded-xl border border-line p-4">
                {isHeldActionError(generate.error) ? (
                  <p className="text-sm text-ink">
                    Generation was refused for this organisation or campaign. Nothing was
                    rendered. Check your assignment before trying again.
                  </p>
                ) : isActionUnavailableError(generate.error) ? (
                  <p className="text-sm text-ink">
                    The generation service is unavailable right now; the attempt was not started
                    here. Refresh to check again.
                  </p>
                ) : isStaleActionError(generate.error) ? (
                  <p className="text-sm text-ink">
                    This attempt cannot continue: its pinned configuration or state no longer
                    matches (it may have been superseded or failed). Refresh the status below for
                    what is known; starting again creates a separate attempt.
                  </p>
                ) : null}
              </div>
            ) : null}
            {attemptStatus === "unknown" ? (
              <div className="rounded-xl border border-dashed border-line p-4">
                <p className="text-sm text-ink">
                  The last attempt's status is unknown: the service could not confirm it. Refresh
                  the status to check again; starting a new attempt would create a separate one
                  and is never done for you.
                </p>
                <div className="mt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    className="min-h-[44px] min-w-[44px]"
                    onClick={newAttempt}
                  >
                    Start a new attempt
                  </Button>
                </div>
              </div>
            ) : null}
            {attemptStatus === "failed" ? (
              <div role="alert" className="rounded-xl border border-dashed border-line p-4">
                <p className="text-sm text-ink">
                  File generation failed. The attempt is terminal: it will not resume on its own.
                  Starting a new attempt is your decision and creates a separate one.
                </p>
                <div className="mt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    className="min-h-[44px] min-w-[44px]"
                    onClick={newAttempt}
                  >
                    Start a new attempt
                  </Button>
                </div>
              </div>
            ) : null}
            {files.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-ink">
                  <caption className="sr-only">Generated export files for this attempt</caption>
                  <thead>
                    <tr className="border-b border-line text-left text-ink-2">
                      <th scope="col" className="py-2 pr-4 font-medium">Platform</th>
                      <th scope="col" className="py-2 pr-4 font-medium">Rows</th>
                      <th scope="col" className="py-2 pr-4 font-medium">Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file) => (
                      <tr key={file.platform} className="border-b border-line">
                        <td className="py-2 pr-4">{file.platform}</td>
                        <td className="py-2 pr-4 tabular-nums">{file.row_count}</td>
                        <td className="py-2 pr-4 tabular-nums">{formatBytes(file.byte_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {files.length > 0 ? (
              <p className="text-sm text-ink-2">
                Private download is not available in this workspace yet. The receipt below shows
                each file's serving state honestly; nothing here uploads or transfers a file.
              </p>
            ) : null}
          </div>
        ) : null}

        {/* ---- Receipts ------------------------------------------------------------ */}
        {audience.attemptId !== null ? (
          <div className="mt-6 space-y-3 rounded-xl border border-line p-4">
            <h4 className="font-semibold text-ink">Receipt</h4>
            {receipts.isPending ? (
              <LoadingState label="Checking the receipt…" />
            ) : receipts.isError ? (
              <p className="text-sm text-ink-2">
                The receipt could not be verified. Refresh the status to check again; this is a
                read and nothing was changed.
              </p>
            ) : receipts.data ? (
              <div className="space-y-3">
                <dl className="grid gap-3 sm:grid-cols-2">
                  {receipts.data.serving.map((fact) => (
                    <div key={fact.platform}>
                      <dt className="text-sm text-ink-2">{fact.platform}</dt>
                      <dd className="mt-1 text-sm tabular-nums text-ink">
                        {fact.minted_tokens} minted / {fact.served_tokens} served ({fact.state})
                      </dd>
                    </div>
                  ))}
                </dl>
                {receipts.data.version ? (
                  <div className="rounded-lg border border-line p-3 text-sm text-ink">
                    <p>
                      Version {receipts.data.version.version_no}:{" "}
                      {versionStateCopy(receipts.data.version.state_label)}
                    </p>
                    {receipts.data.version.handoff ? (
                      <p className="mt-1 text-ink-2">
                        Handoff recorded manually
                        {receipts.data.version.handoff.recorded_at
                          ? ` on ${receipts.data.version.handoff.recorded_at}`
                          : ""}
                        ; the platform has not acknowledged it. Platforms:{" "}
                        {receipts.data.version.handoff.platforms.join(", ")}.{" "}
                        {receipts.data.version.handoff.note}
                      </p>
                    ) : null}
                    {typeof receipts.data.version.unique_identities === "number" ? (
                      <p className="mt-1 text-ink-2">
                        Frozen unique identities: {receipts.data.version.unique_identities}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-sm text-ink-2">
                    These files are not in the download version chain yet.
                  </p>
                )}
                <p className="text-sm text-ink-2">{receipts.data.counts_note}</p>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
